import type { MouseEvent } from 'react'
import { useState } from 'react'

import { Icon, MessagePopup, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Disclosure } from '@skytech/ui/Disclosure'
import { sanitizeAndInsertTargetBlank } from '@skytech/utils/html-sanitization'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import ContentCopyIcon from 'mdi-react/ContentCopyIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { FormattedNumber } from '@/components/FormattedNumber'
import { LocalDate } from '@/components/LocalDate'
import RouterLink from '@/components/RouterLink'
import { APP_ROUTES } from '@/util/app-routes'

import adminQueries from '../queries'
import type { EmployeeConsent } from '../resources'
import adminResources from '../resources'

import { StatusBadge } from './StatusBadge'

type ConsentRowProps = {
  consent: EmployeeConsent
  newestConsentIdByCustomerId: Record<number, EmployeeConsent | null>
  onCopyConsent: (consentId: number) => void
  onEditConsent: (consentId: number) => void
}

export const ConsentRow: React.FC<ConsentRowProps> = ({
  consent,
  newestConsentIdByCustomerId,
  onCopyConsent,
  onEditConsent,
}) => {
  const [openPopup, setOpenPopup] = useState(false)
  const queryClient = useQueryClient()

  const publishEmployeeConsent = useMutation(adminResources.publishEmployeeConsent, {
    onSuccess() {
      queryClient.invalidateQueries(adminQueries.adminConsents())
      setOpenPopup(false)
    },
  })

  const onPopupOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setOpenPopup(true)
  }

  const consentCustomerId = consent.customer?.id
  const isNewestItem = consentCustomerId
    ? newestConsentIdByCustomerId[consentCustomerId]?.id === consent.id
    : false
  const userName = consent.user ? `${consent.user.firstName} ${consent.user.lastName}` : '-'
  const customerName = consent.customer?.name

  return (
    <Disclosure key={consent.id} defaultOpen={isNewestItem}>
      {({ open }) => (
        <div
          className={twJoin(
            'transition-max-height rounded-lg p-4 shadow-lg duration-500 ease-in-out',
            open && 'max-h-[99999px]',
            !open && 'max-h-48'
          )}
        >
          <Disclosure.Button isOpen={open}>
            <div className="mb-2 flex w-full items-center justify-between">
              <StyledText block size="xl" colorMode="black" className="consent-title">
                {consent.title}
              </StyledText>
              <Button
                size="2xs"
                variant="tertiary"
                Icon={<ContentCopyIcon />}
                className="m-0 mb-2 mr-2 p-0"
                onClick={e => {
                  e.stopPropagation()
                  onCopyConsent(consent.id)
                }}
              >
                <Translation id="Copy consent" />
              </Button>
            </div>
            <div className="flex">
              <div className="flex-1">
                <StyledText block marginBottom={4} size="small" colorMode="gray">
                  <Translation
                    className="flex gap-1"
                    id="Created {date} by {userName}"
                    userName={<span className="font-bold">{userName}</span>}
                    date={
                      <span className="font-bold">
                        <LocalDate date={consent.createdDate} />
                      </span>
                    }
                  />
                </StyledText>

                {consentCustomerId && (
                  <div className="mb-1 flex gap-1">
                    <StyledText block capitalize size="small" colorMode="gray">
                      <Translation id="Company" />
                    </StyledText>

                    <StyledText
                      block
                      className="font-bold"
                      capitalize
                      size="small"
                      colorMode="gray"
                    >
                      {customerName}
                    </StyledText>
                  </div>
                )}
                <RouterLink
                  hasUnderline={false}
                  to={APP_ROUTES.ADMIN_EMPLOYEE_CONSENT_REPORT.replace(
                    ':consentId',
                    String(consent.id)
                  )}
                  colorMode="gray"
                  className="consent-reports flex items-baseline gap-0.5"
                >
                  <StyledText size="small" colorMode="gray" className="flex">
                    <Translation
                      id="Consent given by {numOfUsers}/{numOfAllUsers} users"
                      numOfUsers={
                        <StyledText capitalize className="font-bold">
                          <FormattedNumber amount={consent.consentsGiven} />
                        </StyledText>
                      }
                      numOfAllUsers={
                        <StyledText capitalize size="small" className="font-bold">
                          <FormattedNumber amount={consent.consentsTotalToBeGiven} />
                        </StyledText>
                      }
                    />
                  </StyledText>
                  <Icon size="small" className="consent-arrow">
                    <ArrowRightIcon />
                  </Icon>
                </RouterLink>

                {!isNewestItem && (
                  <StyledText block marginTop={8} colorMode="gray">
                    <Translation id="(Inactive)" />
                  </StyledText>
                )}
              </div>

              <div className="w-2/10 flex flex-col items-center justify-between space-x-4">
                <StatusBadge status={consent.employeeConsentStatus.name} />

                {consent.employeeConsentStatus.name === 'Draft' && (
                  <Button
                    variant="secondary"
                    onClick={onPopupOpen}
                    disabled={publishEmployeeConsent.isLoading}
                    isLoading={publishEmployeeConsent.isLoading}
                  >
                    <Translation id="Publish" />
                  </Button>
                )}
              </div>
            </div>
          </Disclosure.Button>

          <Disclosure.Panel className="mb-4 mt-2 whitespace-pre-line rounded-lg">
            <div className="flex">
              <div className="max-h-96 flex-1 overflow-auto rounded-md border border-solid border-gray-200 px-8">
                <span
                  dangerouslySetInnerHTML={{ __html: sanitizeAndInsertTargetBlank(consent.text) }}
                />
              </div>
              {consent.employeeConsentStatus.name === 'Draft' && (
                <div className="w-3/10 flex flex-col items-center justify-between space-x-4">
                  <Button
                    variant="primary"
                    className="h-full"
                    onClick={() => onEditConsent(consent.id)}
                  >
                    <Translation id="Edit" />
                  </Button>
                </div>
              )}
            </div>
            {/* {consent.text} */}
          </Disclosure.Panel>

          <MessagePopup
            className="confirm-policy-sub"
            type="warning"
            title={<Translation id="publish-consent-warning" />}
            variant="narrow"
            message={<Translation className="block" id="publish-consent-body" />}
            isOpen={openPopup}
            onClose={() => setOpenPopup(false)}
          >
            <footer>
              <Button variant="tertiary" onClick={() => setOpenPopup(false)}>
                <Translation id="Cancel" />
              </Button>
              <Button
                isLoading={publishEmployeeConsent.isLoading}
                disabled={publishEmployeeConsent.isLoading}
                onClick={() => publishEmployeeConsent.mutate({ id: String(consent.id) })}
              >
                <Translation id="Confirm" />
              </Button>
            </footer>
          </MessagePopup>
        </div>
      )}
    </Disclosure>
  )
}
